import { useEffect, useState } from "react";
import { Clock, Radio } from "lucide-react";

type Status = "upcoming" | "live" | "ended" | "unknown";

interface Parts {
  status: Status;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  startsAt?: Date;
  endsAt?: Date;
}

function compute(now: Date, startsAt?: string, endsAt?: string, override?: Status | null): Parts {
  const start = startsAt ? new Date(startsAt) : undefined;
  const end = endsAt ? new Date(endsAt) : undefined;
  const safeStart = start && !Number.isNaN(start.getTime()) ? start : undefined;
  const safeEnd = end && !Number.isNaN(end.getTime()) ? end : undefined;

  if (override && override !== "unknown") {
    // Honor manual override for status, but keep countdown numbers based on dates if available.
    const t = now.getTime();
    if (override === "upcoming" && safeStart) {
      const diff = Math.max(0, safeStart.getTime() - t);
      return {
        status: "upcoming",
        days: Math.floor(diff / 86_400_000),
        hours: Math.floor((diff % 86_400_000) / 3_600_000),
        minutes: Math.floor((diff % 3_600_000) / 60_000),
        seconds: Math.floor((diff % 60_000) / 1000),
        startsAt: safeStart,
        endsAt: safeEnd,
      };
    }
    return { status: override, days: 0, hours: 0, minutes: 0, seconds: 0, startsAt: safeStart, endsAt: safeEnd };
  }

  if (!safeStart && !safeEnd) return { status: "unknown", days: 0, hours: 0, minutes: 0, seconds: 0 };

  const t = now.getTime();
  const startT = safeStart?.getTime() ?? -Infinity;
  const endT = safeEnd?.getTime() ?? startT;

  if (t < startT) {
    const diff = startT - t;
    const days = Math.floor(diff / 86_400_000);
    const hours = Math.floor((diff % 86_400_000) / 3_600_000);
    const minutes = Math.floor((diff % 3_600_000) / 60_000);
    const seconds = Math.floor((diff % 60_000) / 1000);
    return { status: "upcoming", days, hours, minutes, seconds, startsAt: safeStart, endsAt: safeEnd };
  }
  if (t <= endT) return { status: "live", days: 0, hours: 0, minutes: 0, seconds: 0, startsAt: safeStart, endsAt: safeEnd };
  return { status: "ended", days: 0, hours: 0, minutes: 0, seconds: 0, startsAt: safeStart, endsAt: safeEnd };
}

function useCountdown(startsAt?: string, endsAt?: string, intervalMs: number = 60_000, override?: Status | null) {
  const [parts, setParts] = useState<Parts | null>(null);

  useEffect(() => {
    setParts(compute(new Date(), startsAt, endsAt, override));
    const id = setInterval(() => setParts(compute(new Date(), startsAt, endsAt, override)), intervalMs);
    return () => clearInterval(id);
  }, [startsAt, endsAt, intervalMs, override]);

  return parts;
}

function shortLabel(p: Parts): string {
  if (p.status === "live") return "Live now";
  if (p.status === "ended") return "Ended";
  if (p.status !== "upcoming") return "";
  if (p.days === 0 && p.hours === 0) return p.minutes <= 1 ? "Starting now" : `in ${p.minutes} min`;
  if (p.days === 0) return p.hours === 1 ? "in 1 hour" : `in ${p.hours} hours`;
  if (p.days === 1) return "Tomorrow";
  if (p.days < 7) return `in ${p.days} days`;
  if (p.days < 30) {
    const w = Math.round(p.days / 7);
    return w === 1 ? "in 1 week" : `in ${w} weeks`;
  }
  const months = Math.round(p.days / 30);
  return months === 1 ? "in 1 month" : `in ${months} months`;
}

interface PillProps {
  startsAt?: string;
  endsAt?: string;
  className?: string;
  tone?: "light" | "dark";
  statusOverride?: Status | null;
  show?: boolean;
}

export function CountdownPill({ startsAt, endsAt, className = "", tone = "light", statusOverride = null, show = true }: PillProps) {
  const p = useCountdown(startsAt, endsAt, 60_000, statusOverride);
  if (!show) return null;
  if (!p || p.status === "unknown") return null;

  const label = shortLabel(p);
  const Icon = p.status === "live" ? Radio : Clock;

  const palette =
    p.status === "live"
      ? "bg-[oklch(0.95_0.1_30)] text-[oklch(0.4_0.18_30)] ring-1 ring-[oklch(0.7_0.18_30)]/40"
      : p.status === "ended"
      ? tone === "dark"
        ? "bg-white/15 text-white/70"
        : "bg-navy/8 text-navy/55"
      : tone === "dark"
      ? "bg-white/90 text-navy ring-1 ring-white/70"
      : "bg-navy text-white";

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] ${palette} ${className}`}
      suppressHydrationWarning
    >
      <Icon className={`size-3 ${p.status === "live" ? "animate-pulse" : ""}`} />
      {label}
    </span>
  );
}

interface TickerProps {
  startsAt?: string;
  endsAt?: string;
  className?: string;
  statusOverride?: Status | null;
  show?: boolean;
}

export function CountdownTicker({ startsAt, endsAt, className = "", statusOverride = null, show = true }: TickerProps) {
  const p = useCountdown(startsAt, endsAt, 1000, statusOverride);
  if (!show) return null;
  if (!p || p.status === "unknown") return null;

  if (p.status === "live") {
    return (
      <div className={`rounded-2xl border border-[oklch(0.7_0.18_30)]/30 bg-[oklch(0.97_0.06_30)] px-5 py-4 ${className}`}>
        <div className="flex items-center gap-2 text-[oklch(0.4_0.18_30)] font-mono text-[10px] uppercase tracking-[0.3em]">
          <Radio className="size-3.5 animate-pulse" /> Happening now
        </div>
        <p className="font-heading text-navy text-2xl mt-1">It's live — go!</p>
      </div>
    );
  }

  if (p.status === "ended") {
    return (
      <div className={`rounded-2xl border border-navy/10 bg-navy/5 px-5 py-4 ${className}`}>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-navy/55">Event ended</p>
        <p className="font-heading text-navy/70 text-lg mt-1">See you next time.</p>
      </div>
    );
  }

  const cells: Array<{ value: number; label: string }> = [
    { value: p.days, label: p.days === 1 ? "day" : "days" },
    { value: p.hours, label: p.hours === 1 ? "hour" : "hours" },
    { value: p.minutes, label: p.minutes === 1 ? "min" : "mins" },
    { value: p.seconds, label: p.seconds === 1 ? "sec" : "secs" },
  ];

  return (
    <div className={`rounded-2xl bg-gradient-to-br from-navy via-navy to-navy-deep text-white px-5 py-4 shadow-lg shadow-navy/20 ${className}`} suppressHydrationWarning>
      <div className="flex items-center gap-2 text-lavender font-mono text-[10px] uppercase tracking-[0.3em]">
        <Clock className="size-3.5" /> Starts in
      </div>
      <div className="mt-2 grid grid-cols-4 gap-2 sm:gap-4">
        {cells.map((c) => (
          <div key={c.label} className="flex flex-col items-center">
            <span className="font-heading text-3xl sm:text-4xl leading-none tabular-nums">
              {String(c.value).padStart(2, "0")}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/60 mt-1">
              {c.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
